import React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Formik, ErrorMessage } from 'formik';
import isArray from 'lodash/isArray';

import LoadMask from 'components/LoadMask';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicSubmitButton from 'components/BasicSubmitButton';
import SurveyRadioGroup from 'components/SurveyRadioGroup';
import BasicLabel from 'components/BasicLabel';
import BasicTextArea from 'components/BasicTextArea';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';

import { useApi } from 'hooks/useApi';
import { ENDPOINT } from 'constants/endpoints';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';
import { ERROR_FIELD } from 'constants/error-fields';

import { RecruiterProcessSurveyFormValidationSchema } from 'scenes/Form/components/RecruiterProcessSurveyForm/validate';
import './styles.css';

const RecruiterProcessSurveyForm = props => {
  const { disableForm, onAfterSave, initialValues } = props;

  const api = useApi();

  const handleSubmit = (values, { setErrors, setSubmitting }) => {
    api.post(ENDPOINT.FORM.SAVE.RECRUITER_PROCESS_SURVEY, values, {
      handleError: response => {
        const {
          data: { fields },
        } = response;
        if (fields && typeof setErrors === 'function') {
          const obj = {};
          for (let [key, value] of Object.entries(fields)) {
            const error = isArray(value) ? value[0] : value;
            obj[key] = ERROR_FIELD[error] || `${error}`;
          }
          setErrors(obj);
        } else {
          ResponseErrorDialog(response);
        }
        setSubmitting(false);
      },
      handleSuccess: () => onAfterSave(),
    });
  };

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={initialValues}
          validationSchema={RecruiterProcessSurveyFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}

              <p className="survey__text" style={{ marginBottom: '20px' }}>
                How likely are you to recommend GoBuyside to others?
              </p>

              <div className="form__group form__group--compact">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <BasicLabel
                    name="Score"
                    required={false}
                    text="Definitely Will"
                  />

                  <BasicLabel
                    name="Score"
                    required={false}
                    text="Definitely Will Not"
                  />
                </div>

                <SurveyRadioGroup
                  name="Score"
                  type="radio"
                  options={['5', '4', '3', '2', '1']}
                  selectedOptions={values.Score || ''}
                  directionRow={true}
                  controlFunc={handleChange}
                  wrapperStyles={{
                    justifyContent: 'space-between',
                  }}
                  labelStyles={{
                    marginRight: '0',
                  }}
                />
                {errors.Score && !!touched.Score && (
                  <div className="form__error">{errors.Score}</div>
                )}
              </div>

              <div className="form__group form__group--compact">
                <BasicLabel
                  name="Comment"
                  required={false}
                  text="We are constantly striving to provide a great client experience, please tell us what we did well and what we could do to improve:"
                  styles={{
                    fontWeight: 'bold',
                    marginTop: '25px',
                  }}
                />
                <BasicTextArea
                  rows={12}
                  name="Comment"
                  content={values.Comment || ''}
                  resize={false}
                  placeholder="0 characters of the 15000 character limit"
                  controlFunc={handleChange}
                  touchFunc={handleBlur}
                  isValid={
                    (!errors.Comment && !!touched.Comment) || !touched.Comment
                  }
                />
                <ErrorMessage
                  name="Comment"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__controls form-controls--compact">
                <BasicButtonGroup
                  groupStyles={{
                    justifyContent: 'center',
                  }}
                >
                  <BasicSubmitButton
                    disabled={disableForm}
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={700}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Submit"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  );
};

export default RecruiterProcessSurveyForm;
